import { useMutation } from '@apollo/client';
import { getNumberOfMonthsInPeriod, isMonthYearBefore, MonthYear, areMonthYearEqual } from '@common/neo.tax';
import { Stack, Typography } from '@ui-components/neo.tax';
import { ReactComponent as RewardStarIcon } from '@ui-components/neo.tax/dist/icons/reward-star-3.svg';
import { useMemo, useState } from 'react';

import {
  Question,
  isUrl,
  QuestionCode,
  useCurrentCompanyId,
  GENERATE_ELEVATOR_PITCH,
  GenerateElevatorPitchMutation,
  GenerateElevatorPitchMutationVariables,
  UpdateShortYearPeriodMutationVariables,
  UPDATE_SHORT_YEAR_PERIOD,
  UpdateShortYearPeriodMutation,
  ChoiceCode,
  QuestionSectionCode,
  periodDisplayName,
  useAlternatingText,
  GENERATING_ELEVATOR_PITCH_TEXTS,
} from '@neo.tax/shared';

import { ActionButtonProps } from '../organisms/company-info/ActionButton';
import { useQuestionSections } from './useQuestionSections';

const GENERATE_ELEVATOR_PITCH_REQUEST_KEY = 'GENERATE_ELEVATOR_PITCH_REQUEST_KEY';
const SUBMIT_SHORT_YEAR_DATA_REQUEST_KEY = 'SUBMIT_SHORT_YEAR_DATA_REQUEST_KEY';

interface UseQuestionActionButtonInput {
  questions: Question[];
  // Temporary fix for MonthYear. This should be removed once the short year question is combined into one (NEO-5604)
  shortYearStartMonthYear?: MonthYear;
  shortYearEndMonthYear?: MonthYear;
  isShortYearDirty?: boolean;
}

export const useQuestionActionButtonProps = ({
  isShortYearDirty,
  questions,
  shortYearEndMonthYear,
  shortYearStartMonthYear,
}: UseQuestionActionButtonInput): Map<QuestionCode, ActionButtonProps | undefined> => {
  const companyId = useCurrentCompanyId();
  const { getAnswer, questionSections } = useQuestionSections();
  const [warningText, setWarningText] = useState<string | undefined>();

  const incorporationDateAnswer = getAnswer(
    questionSections,
    QuestionSectionCode.CompanyHistory,
    QuestionCode.CurrentIncorporationDate,
  );

  const [generateElevatorPitch, { loading: isGeneratingElevatorPitch }] = useMutation<
    GenerateElevatorPitchMutation,
    GenerateElevatorPitchMutationVariables
  >(GENERATE_ELEVATOR_PITCH, {
    context: {
      serializationKey: GENERATE_ELEVATOR_PITCH_REQUEST_KEY,
      debounceKey: GENERATE_ELEVATOR_PITCH_REQUEST_KEY,
      errorToast: {
        title: 'Error',
        description: 'Failed to generate elevator pitch',
        duration: 3000,
      },
      successToast: {
        title: 'Success',
        description: 'Elevator pitch generated',
        duration: 3000,
      },
    },
    refetchQueries: ['GetQuestionSections'],
  });

  const handleGenerateElevatorPitch = async (): Promise<void> => {
    await generateElevatorPitch({
      variables: {
        companyId,
      },
    });
  };

  const [updateShortYearPeriod, { loading: isUpdatingShortYearPeriod }] = useMutation<
    UpdateShortYearPeriodMutation,
    UpdateShortYearPeriodMutationVariables
  >(UPDATE_SHORT_YEAR_PERIOD, {
    context: {
      serializationKey: SUBMIT_SHORT_YEAR_DATA_REQUEST_KEY,
      debounceKey: SUBMIT_SHORT_YEAR_DATA_REQUEST_KEY,
      errorToast: {
        title: 'Error',
        description: 'Failed To Submit Short Year Dates',
        duration: 3000,
      },
    },
    refetchQueries: ['GetQuestionSections'],
  });

  const handleUpdateShortYearPeriod = async (): Promise<void> => {
    if (!shortYearStartMonthYear || !shortYearEndMonthYear) {
      return;
    }
    await updateShortYearPeriod({
      variables: {
        companyId,
        shortYearPeriod: {
          startMonthYear: shortYearStartMonthYear,
          endMonthYear: shortYearEndMonthYear,
        },
      },
    });
  };

  const questionMappingByCode = new Map<QuestionCode, Question>(
    questions.map((question) => [question.questionCode, question]),
  );

  const companyUrlQuestion = questionMappingByCode.get(QuestionCode.CompanyUrl);
  const hadShortYearQuestion = questionMappingByCode.get(QuestionCode.HadShortYear);

  const generatingButtonText = useAlternatingText(GENERATING_ELEVATOR_PITCH_TEXTS, 3000, isGeneratingElevatorPitch);

  const isCompanyUrlValid = useMemo(() => {
    const companyUrl = companyUrlQuestion?.answerValue?.trim() || '';
    return companyUrl && isUrl(companyUrl);
  }, [companyUrlQuestion?.answerValue]);

  // Temporary fix for MonthYear. This should be removed once the short year question is combined into one (NEO-5604)
  const isShortYearValid = useMemo(() => {
    if (!isShortYearDirty) {
      return false;
    }
    if (!shortYearStartMonthYear) {
      return false;
    }
    if (!shortYearEndMonthYear) {
      return false;
    }

    if (shortYearStartMonthYear.month !== 0 && shortYearEndMonthYear.month !== 11) {
      setWarningText('The short tax year period must either start in January or end in December.');
      return false;
    }

    const incorporationDate = incorporationDateAnswer ? new Date(incorporationDateAnswer) : undefined;
    if (incorporationDate) {
      if (shortYearStartMonthYear.year < incorporationDate.getFullYear()) {
        setWarningText('The short tax year period must start on or after the incorporation date.');
        return false;
      }
    }
    if (
      !isMonthYearBefore(shortYearStartMonthYear, shortYearEndMonthYear) &&
      !areMonthYearEqual(shortYearStartMonthYear, shortYearEndMonthYear)
    ) {
      setWarningText('The end month year must be after the start month year');
      return false;
    }
    const distanceBetween = getNumberOfMonthsInPeriod({
      startMonthYear: shortYearStartMonthYear,
      endMonthYear: shortYearEndMonthYear,
    });
    if (distanceBetween >= 12) {
      setWarningText('The short tax year period must be less than 12 months.');
      return false;
    }
    setWarningText(undefined);
    return true;
  }, [incorporationDateAnswer, isShortYearDirty, shortYearEndMonthYear, shortYearStartMonthYear]);

  const hadShortYear = useMemo(() => {
    const hadShortYearChoice = hadShortYearQuestion?.questionChoices?.find((choice) => choice.selected);
    return hadShortYearChoice?.choiceCode === ChoiceCode.Yes;
  }, [hadShortYearQuestion?.questionChoices]);

  const actionButtonPropsMap = new Map<QuestionCode, ActionButtonProps | undefined>();

  for (const question of questions) {
    switch (question.questionCode) {
      case QuestionCode.CompanyElevatorPitch:
        actionButtonPropsMap.set(QuestionCode.CompanyElevatorPitch, {
          disabled: isGeneratingElevatorPitch || !isCompanyUrlValid,
          onClick: handleGenerateElevatorPitch,
          isLoading: isGeneratingElevatorPitch,
          buttonText: 'Auto-generate from Company URL',
          loadingText: generatingButtonText,
          isQuestionValid: !isCompanyUrlValid,
          warningText: 'Please fill in the Company URL to enable auto-generation.',
          startIcon: <RewardStarIcon />,
          position: 'topRight',
        });
        break;
      case QuestionCode.HadShortYear:
        actionButtonPropsMap.set(
          QuestionCode.HadShortYear,
          hadShortYear
            ? {
                disabled: !isShortYearValid,
                isLoading: isUpdatingShortYearPeriod,
                warningText: warningText,
                buttonText: 'Submit Short Year Data',
                loadingText: 'Submitting...',
                position: 'bottom',
                actionButtonModalProps: {
                  title: 'Confirm Short Year Data Change',
                  description: (
                    <Stack direction="column" gap={3}>
                      <Typography as="h3">
                        Are you sure these dates are correct? Changing these dates will require you to re-enter your
                        prior year revenue and expenses.
                      </Typography>
                      {shortYearStartMonthYear &&
                        shortYearEndMonthYear && ( // We can assume these will always be populated considering the fact that the button is disabled if the short year is not valid
                          <Typography as="p">
                            Dates you selected:{' '}
                            <Typography as="b" fontWeight="bold">
                              {periodDisplayName(
                                {
                                  startMonthYear: shortYearStartMonthYear,
                                  endMonthYear: shortYearEndMonthYear,
                                },
                                true,
                              )}
                            </Typography>
                          </Typography>
                        )}
                    </Stack>
                  ),
                  isLoading: isUpdatingShortYearPeriod,
                  closeModal: (): void => {},
                  confirmMutation: handleUpdateShortYearPeriod,
                  confirmButtonText: 'Confirm',
                  loadingText: 'Submitting...',
                  cancelButtonText: 'Cancel',
                },
              }
            : undefined,
        );
        break;
      default:
        actionButtonPropsMap.set(question.questionCode, undefined);
        break;
    }
  }
  return actionButtonPropsMap;
};
